import { useCallback } from 'react';
import { InfiniteData } from '@antimatterhq/ui/hooks/queries/useInfiniteQuery';
import { useQueryClient } from '@antimatterhq/ui/hooks/queries/useQueryClient';
import { common_v1_ObjectMeta } from '@antimatterhq/asl-api-ts-v1';
import { ListResponse } from '@/types/listResponse';

export const useOptimisticUpdateFromInfiniteQuery = <
  TItem extends { metadata?: common_v1_ObjectMeta | null },
  TResponse extends ListResponse<TItem>,
>(
  queryKey: unknown[],
  limit: number,
  getItemKey?: (item: TItem) => string,
) => {
  const queryClient = useQueryClient();

  const optimisticUpdate = useCallback(
    (item: TItem) => {
      queryClient.setQueryData(
        queryKey,
        (prev: InfiniteData<TResponse> | undefined) => {
          if (!prev) return prev;

          const itemExists = prev.pages.some((page) =>
            page.items.some((existingItem) =>
              getItemKey !== undefined
                ? getItemKey(existingItem) === getItemKey(item)
                : existingItem.metadata?.name === item.metadata?.name,
            ),
          );

          if (itemExists) {
            // Update existing item
            const updatedPages = prev.pages.map((page) => {
              return {
                ...page,
                items: page.items.map((existingItem) =>
                  getItemKey !== undefined
                    ? getItemKey(existingItem) === getItemKey(item)
                      ? item
                      : existingItem
                    : existingItem.metadata?.name === item.metadata?.name
                      ? item
                      : existingItem,
                ),
              } as TResponse;
            });

            return {
              ...prev,
              pages: updatedPages,
            };
          } else {
            // Add new item
            const lastPage = prev.pages[prev.pages.length - 1];

            if (!lastPage) {
              // If there are no pages, create a new one
              return {
                ...prev,
                pages: [
                  {
                    items: [item],
                    metadata: { cursor: null },
                  } as TResponse,
                ],
              };
            }

            const canAddToLastPage = lastPage.items.length < limit;

            if (canAddToLastPage) {
              const updatedPages = [...prev.pages];
              updatedPages[updatedPages.length - 1] = {
                ...lastPage,
                items: [...lastPage.items, item],
              } as TResponse;

              return {
                ...prev,
                pages: updatedPages,
              };
            } else {
              // Last page is full, need to add a new page
              const lastPageHasCursor = lastPage.metadata?.cursor != null;

              if (lastPageHasCursor) {
                return {
                  ...prev,
                  pages: [
                    ...prev.pages,
                    {
                      items: [item],
                      apiVersion: lastPage.apiVersion,
                      kind: lastPage.kind,
                      metadata: { cursor: null },
                    } as TResponse,
                  ],
                };
              } else {
                // TODO: Rare case, last page doesn't have a cursor, probably need to refetch
                return prev;
              }
            }
          }
        },
      );
    },
    [queryClient, queryKey, limit, getItemKey],
  );

  return optimisticUpdate;
};
